var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import * as React from "react";
import classNames from "classnames";
import memoize from "./memoize";
import InlineCard from "./InlineCard";
import { cardLookup, cardAriaLabel } from "./util/cardHelpers";
import { SettingsContext } from "./AppStateProvider";
import { useEngine } from "./useEngine";
import { cardInfoCache, getTrumpKey, prefillCardInfoCache, getPrefillPromise, } from "./util/cachePrefill";
var SvgCard = React.lazy(function () { return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_a) {
    switch (_a.label) {
        case 0: return [4 /*yield*/, import("./SvgCard")];
        case 1: return [2 /*return*/, _a.sent()];
    }
}); }); });
var Card = function (props) {
    var _a;
    var settings = React.useContext(SettingsContext);
    var engine = useEngine();
    var _b = React.useState(null), cardInfo = _b[0], setCardInfo = _b[1];
    var _c = React.useState(false), isLoading = _c[0], setIsLoading = _c[1];
    var height = props.smaller ? 95 : 120;
    var bounds = getCardBounds(height);
    // Create a cache key for the card info based on card and trump
    var cacheKey = "".concat(props.card, "_").concat(getTrumpKey(props.trump));
    React.useEffect(function () {
        // Only load card info if the card is in the lookup
        if (!(props.card in cardLookup)) {
            return;
        }
        // Check cache first
        if (cacheKey in cardInfoCache) {
            setCardInfo(cardInfoCache[cacheKey]);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        // Check if a prefill is already in progress for this trump
        var existingPrefillPromise = getPrefillPromise(props.trump);
        if (existingPrefillPromise) {
            // Wait for existing prefill
            existingPrefillPromise
                .then(function () {
                // Check if our card is now cached
                if (cacheKey in cardInfoCache) {
                    setCardInfo(cardInfoCache[cacheKey]);
                    setIsLoading(false);
                }
                else {
                    // If still not cached after prefill, something went wrong
                    console.error("Card ".concat(props.card, " not in cache after prefill completed"));
                    var staticInfo = cardLookup[props.card];
                    setCardInfo({
                        suit: null,
                        effective_suit: "Unknown",
                        value: staticInfo.value || props.card,
                        display_value: staticInfo.display_value || props.card,
                        typ: staticInfo.typ || props.card,
                        number: staticInfo.number || null,
                        points: staticInfo.points || 0,
                    });
                    setIsLoading(false);
                }
            })
                .catch(function (error) {
                console.error("Failed to wait for prefill:", error);
                setIsLoading(false);
            });
            return;
        }
        // Check if we should trigger a full prefill for this trump
        var trumpKey = getTrumpKey(props.trump);
        // Count how many cards are cached for this trump
        var cachedCount = Object.keys(cardInfoCache).filter(function (key) {
            return key.endsWith("_".concat(trumpKey));
        }).length;
        // If we have very few cached cards for this trump, prefill everything
        if (cachedCount < 5) {
            // Trigger full prefill for uncached trump
            // Start the prefill and wait for it
            prefillCardInfoCache(engine, props.trump)
                .then(function () {
                // Check if our card is now cached
                if (cacheKey in cardInfoCache) {
                    setCardInfo(cardInfoCache[cacheKey]);
                    setIsLoading(false);
                }
                else {
                    // Fallback if card still not in cache
                    var staticInfo = cardLookup[props.card];
                    setCardInfo({
                        suit: null,
                        effective_suit: "Unknown",
                        value: staticInfo.value || props.card,
                        display_value: staticInfo.display_value || props.card,
                        typ: staticInfo.typ || props.card,
                        number: staticInfo.number || null,
                        points: staticInfo.points || 0,
                    });
                    setIsLoading(false);
                }
            })
                .catch(function (error) {
                console.error("Failed to prefill cache:", error);
                // Fallback on error
                var staticInfo = cardLookup[props.card];
                setCardInfo({
                    suit: null,
                    effective_suit: "Unknown",
                    value: staticInfo.value || props.card,
                    display_value: staticInfo.display_value || props.card,
                    typ: staticInfo.typ || props.card,
                    number: staticInfo.number || null,
                    points: staticInfo.points || 0,
                });
                setIsLoading(false);
            });
            return;
        }
        // Only make individual request if no prefill is needed
        engine
            .batchGetCardInfo({
            requests: [
                {
                    card: props.card,
                    trump: props.trump,
                },
            ],
        })
            .then(function (response) {
            if (!response || !response.results || response.results.length === 0) {
                console.error("Invalid response from batchGetCardInfo:", response);
                // Fallback to basic info from static lookup
                var staticInfo = cardLookup[props.card];
                setCardInfo({
                    suit: null,
                    effective_suit: "Unknown",
                    value: staticInfo.value || props.card,
                    display_value: staticInfo.display_value || props.card,
                    typ: staticInfo.typ || props.card,
                    number: staticInfo.number || null,
                    points: staticInfo.points || 0,
                });
                setIsLoading(false);
                return;
            }
            var info = response.results[0];
            if (!info) {
                console.error("Card info is undefined in response:", response);
                // Fallback to basic info from static lookup
                var staticInfo = cardLookup[props.card];
                setCardInfo({
                    suit: null,
                    effective_suit: "Unknown",
                    value: staticInfo.value || props.card,
                    display_value: staticInfo.display_value || props.card,
                    typ: staticInfo.typ || props.card,
                    number: staticInfo.number || null,
                    points: staticInfo.points || 0,
                });
                setIsLoading(false);
                return;
            }
            // Cache the result with the trump-specific key
            cardInfoCache[cacheKey] = info;
            setCardInfo(info);
            setIsLoading(false);
        })
            .catch(function (error) {
            console.error("Error getting card info:", error);
            console.error("Error stack:", error.stack);
            // Fallback to basic info from static lookup
            var staticInfo = cardLookup[props.card];
            setCardInfo({
                suit: null,
                effective_suit: "Unknown",
                value: staticInfo.value || props.card,
                display_value: staticInfo.display_value || props.card,
                typ: staticInfo.typ || props.card,
                number: staticInfo.number || null,
                points: staticInfo.points || 0,
            });
            setIsLoading(false);
        });
    }, [cacheKey, props.card, props.trump, engine]);
    if (!(props.card in cardLookup)) {
        var nonSVG = (React.createElement("div", { className: classNames("card", "unknown", props.className), role: "img", "aria-label": cardAriaLabel(props.card), style: {
                marginRight: props.collapseRight ? "-".concat(bounds.width * 0.6, "px") : "0",
            } },
            React.createElement(CardCanvas, { card: props.card, height: height, suit: classNames("unknown", settings.fourColor ? "four-color" : null, settings.darkMode ? "dark-mode" : null), backgroundColor: settings.darkMode ? "#000" : "#fff" })));
        if (settings.svgCards) {
            return (React.createElement(React.Suspense, { fallback: nonSVG },
                React.createElement("div", { className: classNames("card", "svg", "unknown", props.className), role: "img", "aria-label": cardAriaLabel(props.card), style: {
                        marginRight: props.collapseRight
                            ? "-".concat(bounds.width * 0.6, "px")
                            : "0",
                    } },
                    React.createElement(SvgCard, { fourColor: settings.fourColor, height: height, card: "🂠" }))));
        }
        else {
            return nonSVG;
        }
    }
    else {
        var staticCardInfo = cardLookup[props.card];
        // Trump/level-aware accessible label + keyboard activation for selectable
        // cards. When the card has an onClick, expose it as a focusable button.
        var ariaLabel = cardAriaLabel(props.card, {
            isTrump: (cardInfo === null || cardInfo === void 0 ? void 0 : cardInfo.effective_suit) === "Trump",
            isLevel: cardInfo !== null &&
                cardInfo.number !== null &&
                cardInfo.effective_suit === "Trump" &&
                staticCardInfo.typ !== "🃏" &&
                staticCardInfo.typ !== "🃟",
            points: cardInfo === null || cardInfo === void 0 ? void 0 : cardInfo.points,
        });
        var interactive = props.onClick !== undefined;
        var interactiveProps = interactive
            ? {
                role: "button",
                tabIndex: 0,
                "aria-pressed": ((_a = props.className) === null || _a === void 0 ? void 0 : _a.includes("selected"))
                    ? true
                    : undefined,
                onKeyDown: function (event) {
                    var _a;
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props, event);
                    }
                },
            }
            : { role: "img" };
        var label = function (offset) {
            if (isLoading || !cardInfo)
                return null;
            return (React.createElement("div", { className: "card-label", style: { bottom: "".concat(offset, "px") } },
                React.createElement(InlineCard, { card: props.card })));
        };
        var icon = function (offset) {
            if (isLoading || !cardInfo)
                return null;
            return (React.createElement("div", { className: "card-icon", style: { bottom: "".concat(offset, "px") } },
                cardInfo.effective_suit === "Trump" && settings.trumpCardIcon,
                cardInfo.points > 0 && settings.pointCardIcon));
        };
        var nonSVG = (React.createElement("div", __assign({ className: classNames("card", staticCardInfo.typ, props.className, isLoading ? "loading" : null) }, interactiveProps, { "aria-label": ariaLabel, onClick: props.onClick, onMouseEnter: props.onMouseEnter, onMouseLeave: props.onMouseLeave, style: {
                marginRight: props.collapseRight ? "-".concat(bounds.width * 0.6, "px") : "0",
            } }),
            label(bounds.height / 10),
            icon(bounds.height),
            React.createElement(CardCanvas, { card: staticCardInfo.display_value, height: height, suit: classNames(staticCardInfo.typ, settings.fourColor ? "four-color" : null, settings.darkMode ? "dark-mode" : null), colorOverride: settings.suitColorOverrides[staticCardInfo.typ], backgroundColor: settings.darkMode ? "#000" : "#fff" })));
        if (settings.svgCards) {
            return (React.createElement(React.Suspense, { fallback: nonSVG },
                React.createElement("div", __assign({ className: classNames("card", "svg", staticCardInfo.typ, props.className) }, interactiveProps, { "aria-label": ariaLabel, onClick: props.onClick, onMouseEnter: props.onMouseEnter, onMouseLeave: props.onMouseLeave, style: {
                        marginRight: props.collapseRight
                            ? "-".concat(bounds.width * 0.6, "px")
                            : "0",
                    } }),
                    label(height / 10),
                    icon(height),
                    React.createElement(SvgCard, { fourColor: settings.fourColor, height: height, card: props.card }))));
        }
        else {
            return nonSVG;
        }
    }
};
var computeCanvasBounds = function (font, dpr) {
    var c = document.createElement("canvas");
    c.style.display = "none";
    document.body.appendChild(c);
    var ctx = c.getContext("2d");
    if (ctx === null) {
        throw new Error("Could not get 2d context");
    }
    ctx.scale(dpr, dpr);
    ctx.font = font;
    var text = "🂠";
    var textMetrics = ctx.measureText(text);
    document.body.removeChild(c);
    return textMetrics;
};
var computeSuitColor = function (suit) {
    var c = document.createElement("div");
    c.className = suit;
    c.style.display = "none";
    document.body.appendChild(c);
    var color = getComputedStyle(c).color;
    document.body.removeChild(c);
    return color;
};
var cardBoundsCache = {};
var suitColorCache = {};
var getCardBounds = function (height) {
    var font = "".concat(height, "px solid");
    if (!(font in cardBoundsCache)) {
        cardBoundsCache[font] = memoize(function () { return computeCanvasBounds(font, 1); });
    }
    var textMetrics = cardBoundsCache[font]();
    var effectiveHeight = Math.round(textMetrics.actualBoundingBoxAscent +
        textMetrics.actualBoundingBoxDescent +
        2);
    var effectiveWidth = Math.round(textMetrics.actualBoundingBoxRight +
        Math.min(textMetrics.actualBoundingBoxLeft, 0) +
        2);
    return {
        metrics: textMetrics,
        height: effectiveHeight,
        width: effectiveWidth,
    };
};
var CardCanvas = function (props) {
    if (!(props.suit in suitColorCache)) {
        suitColorCache[props.suit] = memoize(function () { return computeSuitColor(props.suit); });
    }
    var _a = getCardBounds(props.height), metrics = _a.metrics, width = _a.width, height = _a.height;
    var style = suitColorCache[props.suit]();
    return (React.createElement("svg", { focusable: "false", role: "img", xmlns: "http://www.w3.org/2000/svg", height: height, width: width },
        React.createElement("rect", { fill: props.backgroundColor !== undefined ? props.backgroundColor : "#fff", x: metrics.actualBoundingBoxLeft, y: 0, width: metrics.width - 2, height: height }),
        React.createElement("text", { fill: props.colorOverride !== undefined ? props.colorOverride : style, fontSize: "".concat(props.height, "px"), textLength: "".concat(width, "px"), x: Math.min(metrics.actualBoundingBoxLeft, 0) + 1, y: height - metrics.actualBoundingBoxDescent - 1 }, props.card)));
};
export default Card;
